import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Globe, Check } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";
import { useLang } from "@/lib/LanguageContext";
import { LANGUAGES } from "@/lib/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { lang, setLang, t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { to: "/products", label: t.nav.products, key: "products" },
    { to: "/services", label: t.nav.services, key: "services" },
    { to: "/industries", label: t.nav.industries, key: "industries" },
    { to: "/innovation", label: t.nav.innovation, key: "innovation" },
    { to: "/sustainability", label: t.nav.sustainability, key: "sustainability" },
    { to: "/about", label: t.nav.about, key: "about" },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-border"
          : "bg-white/60 backdrop-blur-md"
      }`}
    >
      <div className="container-x flex items-center justify-between gap-6 h-24">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 shrink-0" aria-label="BALTIVEX home">
          <img src={LOGO_URL} alt="BALTIVEX" className="h-20 w-20 object-contain" style={{ mixBlendMode: "multiply" }} />
          <span className="hidden xl:inline font-display tracking-tight text-primary font-medium text-xl">BALTIVEX</span>
        </Link>

        <nav className="hidden lg:flex items-center lg:gap-5 xl:gap-9 min-w-0">
          {navLinks.map((link) => (
            <NavLink
              key={link.key}
              to={link.to}
              data-testid={`nav-link-${link.key}`}
              className={({ isActive }) =>
                `text-[13px] tracking-wide font-medium transition-colors whitespace-nowrap ${
                  isActive ? "text-primary" : "text-muted-foreground hover:text-primary"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4 shrink-0">
          {/* Language switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="lang-switch-trigger"
                className="inline-flex items-center gap-2 text-[12px] tracking-wide font-medium text-muted-foreground hover:text-primary transition-colors px-2 py-2 focus:outline-none"
                aria-label="Change language"
              >
                <Globe className="w-4 h-4" />
                <span className="tabular-nums">{lang.toUpperCase()}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              data-testid="lang-switch-menu"
              className="min-w-[120px] rounded-none border-border"
            >
              {LANGUAGES.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={`text-[13px] cursor-pointer rounded-none flex items-center justify-between ${
                    lang === l.code ? "text-primary font-medium" : "text-muted-foreground"
                  }`}
                >
                  <span>{l.label}</span>
                  {lang === l.code && <Check className="w-3.5 h-3.5 text-accent" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link
            to="/contact"
            data-testid="nav-link-contact"
            className="group inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 text-[13px] font-medium tracking-wide hover:bg-primary/90 transition-all"
          >
            {t.nav.cta}
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button
          onClick={() => setOpen((s) => !s)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          className="lg:hidden p-2 text-primary"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-border" data-testid="nav-mobile-menu">
          <div className="container-x py-6 flex flex-col gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.key}
                to={link.to}
                data-testid={`nav-mobile-link-${link.key}`}
                className={({ isActive }) =>
                  `py-3 border-b border-border text-base ${
                    isActive ? "text-primary font-medium" : "text-muted-foreground"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}

            {/* Mobile language switcher */}
            <div className="py-4 border-b border-border flex items-center gap-3">
              <Globe className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Language</span>
              <div className="ml-auto flex items-center gap-1">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    data-testid={`lang-mobile-option-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className={`px-3 py-1.5 text-xs tracking-wide border ${
                      lang === l.code
                        ? "bg-primary text-white border-primary"
                        : "border-border text-muted-foreground"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>

            <Link
              to="/contact"
              data-testid="nav-mobile-link-contact"
              className="mt-5 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-5 py-3 text-sm font-medium"
            >
              {t.nav.cta} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
